import { Button } from "@/components/ui/button"
import { RefreshCw, ArrowLeft, AlertTriangle, ShieldAlert, WifiOff, FileQuestion } from "lucide-react"
import { useNavigate } from "react-router-dom"

interface ErrorStateProps {
  code?: number | string | unknown
  title?: string
  message?: string
  onAction?: () => void
  actionLabel?: string
  showbackButton?: boolean
}

export const ErrorState = ({ code, title, message, onAction, actionLabel, showbackButton = true }: ErrorStateProps) => {
  const navigate = useNavigate()

  let Icon = AlertTriangle
  let defTitle = "Something went wrong"
  let defMessage = "An unexpected error occurred. Please try again later."

  const strCode = String(code)

  if (strCode.includes("404")) {
    Icon = FileQuestion
    defTitle = "Page Not Found"
    defMessage = "The page you are looking for doesn't exist or has been moved."
  } else if (strCode.includes("500")) {
    Icon = AlertTriangle
    defTitle = "Server Error"
    defMessage = "Our servers are experiencing issues. Please try again later."
  } else if (strCode.includes("403")) {
    Icon = ShieldAlert
    defTitle = "Access Denied"
    defMessage = "You don't have permission to access this resource."
  } else if (strCode.toLowerCase().includes("network")) {
    Icon = WifiOff
    defTitle = "Connection Error"
    defMessage = "Please check your internet connection and try again."
  }

  return (
    <div className="flex flex-col items-center gap-4 justify-center py-16 px-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col items-center gap-3">
        <div className="relative group cursor-default">
          <div className="absolute inset-0 bg-primary-50 rounded-full scale-[1.8] opacity-20 group-hover:scale-[2.2] transition-transform duration-700 ease-out" />
          <div className="absolute inset-0 bg-primary-50 rounded-full scale-[1.4] opacity-40 group-hover:scale-[1.6] transition-transform duration-500 ease-out" />
          <div className="relative bg-white p-4 rounded-full ring-1 ring-gray-100 shadow-sm group-hover:shadow-md transition-all duration-300">
            <Icon className="h-8 w-8 text-primary-500 transition-colors duration-300" />
          </div>
        </div>

        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 tracking-tight">{title || defTitle}</h3>
          <p className="text-sm text-gray-500 text-center max-w-sm leading-relaxed">{message || defMessage}</p>
        </div>
      </div>

      <div className="flex gap-3 mt-2">
        {showbackButton && (
          <Button
            onClick={() => navigate(-1)}
            size="sm"
            variant="outline"
            className="rounded-full px-5 border-gray-200 hover:bg-gray-50 hover:text-gray-900 transition-all active:scale-95"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
          </Button>
        )}

        {onAction && actionLabel && (
          <Button
            onClick={onAction}
            size="sm"
            className="rounded-full px-5 bg-primary-500 hover:bg-primary-600 text-white shadow-sm hover:shadow transition-all active:scale-95"
          >
            {actionLabel === "Retry" ? <RefreshCw className="mr-2 h-4 w-4" /> : null}
            {actionLabel}
          </Button>
        )}
      </div>
    </div>
  )
}

export default ErrorState
